import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  Minus,
  Search,
  Pencil,
  Trash2,
  Package,
  QrCode,
  Download,
  Upload,
  MapPin,
  DollarSign,
  AlertTriangle,
  Clock,
  Lock,
  Unlock,
  CheckCircle,
  XCircle,
  Filter,
  MoreVertical,
  Printer,
  ShoppingCart,
  Calendar,
  Box,
  Loader2,
  Sparkles,
  X,
  Timer,
  FileSpreadsheet,
  ExternalLink,
  TrendingDown,
  Boxes,
  Edit3,
  Save
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { InventoryBulkDelete } from "@/components/InventoryBulkDelete";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-300",
  received: "bg-blue-100 text-blue-800 border-blue-300",
  available: "bg-green-100 text-green-800 border-green-300",
  hold: "bg-purple-100 text-purple-800 border-purple-300",
  low_stock: "bg-red-100 text-red-800 border-red-300"
};

const STATUS_LABELS = {
  pending: "Pending",
  received: "Received",
  available: "Available",
  hold: "On Hold",
  low_stock: "Low Stock"
};

const initialFormData = {
  name: "",
  description: "",
  location: "",
  sub_location: "",
  location_quantities: [],
  quantity: 0,
  minimum_stock: null,
  has_expiration: false,
  expiration_date: "",
  cost_per_piece: 0,
  library_use_only: false,
  notes: "",
  existing_qr_code: "",
  purchase_link: "",
  container_type: "none",
  container_size: "none"
};

export const Inventory = ({ user, viewMode }) => {
  const [items, setItems] = useState([]);
  const [reorderList, setReorderList] = useState([]);
  const [recentItems, setRecentItems] = useState([]);
  const [myHolds, setMyHolds] = useState([]);
  const [locations, setLocations] = useState({ locations: [], sub_locations: {} });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [holdDialogOpen, setHoldDialogOpen] = useState(false);
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [isEditing, setIsEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [locationFilter, setLocationFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [containerFilter, setContainerFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("inventory");
  const [holdReason, setHoldReason] = useState("");
  const [holdLocation, setHoldLocation] = useState("");
  const [receiveData, setReceiveData] = useState({ location: "", sub_location: "", quantity: null });
  const [useExistingQr, setUseExistingQr] = useState(false);
  const [showNewItemsCard, setShowNewItemsCard] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const fileInputRef = useRef(null);
  
  // Quick adjustment dialog states
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickSubtractOpen, setQuickSubtractOpen] = useState(false);
  const [quickAddData, setQuickAddData] = useState({ location: "", sub_location: "", quantity: 1, isNewLocation: false });
  const [quickSubtractData, setQuickSubtractData] = useState({ location: "", quantity: 1 });

  // Supply inventory / budget tracking states
  const [supplyData, setSupplyData] = useState(null);
  const [editingBudget, setEditingBudget] = useState(false);
  const [budgetInput, setBudgetInput] = useState("");

  const isSupervisor = user?.role === "supervisor" || user?.is_admin;
  const isAdmin = user?.is_admin;

  useEffect(() => {
    fetchData();
    fetchSupplyData();
  }, []);

  const fetchSupplyData = async () => {
    try {
      const res = await axios.get(`${API}/supplies/inventory`);
      setSupplyData(res.data);
      setBudgetInput(res.data.available_funds?.toString() || "0");
    } catch (error) {
      // Silently fail - supply data is optional
    }
  };

  const handleUpdateBudget = async () => {
    try {
      const newBudget = parseFloat(budgetInput) || 0;
      await axios.put(`${API}/budget`, { available_funds: newBudget });
      toast.success("Budget updated successfully");
      setEditingBudget(false);
      fetchSupplyData();
    } catch (error) {
      toast.error("Failed to update budget");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const fetchData = async () => {
    try {
      const [itemsRes, locationsRes, reorderRes, recentRes, holdsRes] = await Promise.all([
        axios.get(`${API}/inventory`),
        axios.get(`${API}/inventory/locations`),
        axios.get(`${API}/reorder-list`),
        axios.get(`${API}/inventory/recent`),
        axios.get(`${API}/inventory/my-holds`)
      ]);
      setItems(itemsRes.data);
      setLocations(locationsRes.data);
      setReorderList(reorderRes.data);
      setRecentItems(recentRes.data);
      setMyHolds(holdsRes.data);
    } catch (error) {
      toast.error("Failed to fetch inventory");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Please enter an item name");
      return;
    }

    try {
      // Calculate total quantity from location_quantities if present
      const totalFromLocations = formData.location_quantities?.length > 0
        ? formData.location_quantities.reduce((sum, lq) => sum + (parseFloat(lq.quantity) || 0), 0)
        : parseFloat(formData.quantity) || 0;

      const payload = {
        ...formData,
        minimum_stock: formData.minimum_stock || null,
        cost_per_piece: parseFloat(formData.cost_per_piece) || 0,
        quantity: totalFromLocations,
        location_quantities: formData.location_quantities || [],
        existing_qr_code: useExistingQr ? formData.existing_qr_code : null
      };

      if (isEditing && selectedItem) {
        await axios.put(`${API}/inventory/${selectedItem.id}`, payload);
        toast.success("Item updated!");
      } else {
        await axios.post(`${API}/inventory`, payload);
        toast.success("Item created!");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save item");
    }
  };

  const handleDelete = async () => {
    if (!selectedItem) return;
    try {
      await axios.delete(`${API}/inventory/${selectedItem.id}`);
      toast.success("Item deleted");
      setDeleteDialogOpen(false);
      setSelectedItem(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete item");
    }
  };

  const handleHold = async () => {
    if (!selectedItem) return;
    try {
      await axios.post(`${API}/inventory/${selectedItem.id}/hold`, { 
        reason: holdReason,
        location: holdLocation
      });
      toast.success("Item placed on hold");
      setHoldDialogOpen(false);
      setHoldReason("");
      setHoldLocation("");
      setSelectedItem(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to hold item");
    }
  };

  const handleRelease = async (item) => {
    try {
      await axios.post(`${API}/inventory/${item.id}/release`);
      toast.success("Item released from hold");
      fetchData();
    } catch (error) {
      toast.error("Failed to release item");
    }
  };

  const handleReceive = async () => {
    if (!selectedItem || !receiveData.location) {
      toast.error("Please select a location");
      return;
    }
    try {
      await axios.post(`${API}/inventory/${selectedItem.id}/receive`, receiveData);
      toast.success("Item received and shelved!");
      setReceiveDialogOpen(false);
      setReceiveData({ location: "", sub_location: "", quantity: null });
      setSelectedItem(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to receive item");
    }
  };

  const handleAddToReorder = async (item) => {
    try {
      await axios.post(`${API}/reorder-list`, {
        inventory_item_id: item.id,
        quantity_needed: 1,
        notes: ""
      });
      toast.success("Added to reorder list");
      fetchData();
    } catch (error) {
      toast.error("Failed to add to reorder list");
    }
  };

  const handleRemoveFromReorder = async (reorderItem) => {
    try {
      await axios.delete(`${API}/reorder-list/${reorderItem.id}`);
      toast.success("Removed from reorder list");
      fetchData();
    } catch (error) {
      toast.error("Failed to remove from reorder list");
    }
  };

  const handleUpdateReorderQuantity = async (reorderItem, newQuantity) => {
    if (newQuantity < 1) {
      toast.error("Quantity must be at least 1");
      return;
    }
    try {
      const response = await axios.put(`${API}/reorder-list/${reorderItem.id}/quantity?quantity=${newQuantity}`);
      setReorderList(prev => prev.map(item => 
        item.id === reorderItem.id ? { ...item, quantity_needed: newQuantity } : item
      ));
      toast.success("Quantity updated");
    } catch (error) {
      toast.error("Failed to update quantity");
    }
  };

  const handleDownloadQR = async (item) => {
    try {
      const response = await axios.get(`${API}/inventory/${item.id}/label`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `label_${item.qr_code}.png`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Label downloaded!");
    } catch (error) {
      toast.error("Failed to download label");
    }
  };

  const handleExportCSV = async () => {
    try {
      const url = locationFilter === "all" 
        ? `${API}/inventory/export/csv`
        : `${API}/inventory/export/csv?location=${encodeURIComponent(locationFilter)}`;
      const response = await axios.get(url, { responseType: 'blob' });
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `inventory_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("CSV exported!");
    } catch (error) {
      toast.error("Failed to export CSV");
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/inventory/template/csv`, { responseType: 'blob' });
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', 'inventory_upload_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Template downloaded!");
    } catch (error) {
      toast.error("Failed to download template");
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toast.error("Please select a CSV file");
        return;
      }
      setUploadFile(file);
      setUploadResults(null);
    }
  };

  const handleUploadCSV = async (skipDuplicates = true) => {
    if (!uploadFile) {
      toast.error("Please select a file first");
      return;
    }

    setUploadLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      
      const response = await axios.post(
        `${API}/inventory/upload/csv?skip_duplicates=${skipDuplicates}`, 
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setUploadResults(response.data);
      
      if (response.data.imported > 0) {
        toast.success(`Successfully imported ${response.data.imported} items!`);
        fetchData(); // Refresh the inventory list
      }
      
      if (response.data.skipped_duplicates > 0) {
        toast.warning(`${response.data.skipped_duplicates} duplicates skipped`);
      }
      
      if (response.data.failed > 0) {
        toast.error(`${response.data.failed} items failed to import`);
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to upload CSV";
      toast.error(message);
    } finally {
      setUploadLoading(false);
    }
  };

  const resetUploadDialog = () => {
    setUploadFile(null);
    setUploadResults(null);
    setUploadDialogOpen(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setFormData({
      name: item.name,
      description: item.description || "",
      location: item.location || "",
      sub_location: item.sub_location || "",
      location_quantities: item.location_quantities || [],
      quantity: item.quantity || 0,
      minimum_stock: item.minimum_stock || null,
      has_expiration: item.has_expiration || false,
      expiration_date: item.expiration_date || "",
      cost_per_piece: item.cost_per_piece || 0,
      library_use_only: item.library_use_only || false,
      notes: item.notes || "",
      existing_qr_code: "",
      purchase_link: item.purchase_link || "",
      container_type: item.container_type || "none",
      container_size: item.container_size || "none"
    });
    setIsEditing(true);
    setUseExistingQr(false);
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setSelectedItem(null);
    setIsEditing(false);
    setUseExistingQr(false);
  };

  const getSubLocations = (location) => {
    return locations.sub_locations[location] || [];
  };

  // Filter items
  const filteredItems = items.filter(item => {
    const matchesSearch = 
      item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.qr_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesLocation = locationFilter === "all" || item.location === locationFilter;
    const matchesStatus = statusFilter === "all" || item.status === statusFilter;
    const matchesContainer = containerFilter === "all" || 
      (containerFilter === "box" && item.container_type === "box") ||
      (containerFilter === "bag" && item.container_type === "bag") ||
      (containerFilter === "none" && (!item.container_type || item.container_type === "none"));
    return matchesSearch && matchesLocation && matchesStatus && matchesContainer;
  });

  const visibleSelectedIds = filteredItems.filter(i => selectedIds.includes(i.id)).map(i => i.id);
  const allVisibleSelected = filteredItems.length > 0 && visibleSelectedIds.length === filteredItems.length;
  const toggleSelectAll = (checked) => setSelectedIds(checked ? filteredItems.map(i => i.id) : []);
  const toggleSelect = (id, checked) =>
    setSelectedIds(prev => checked ? [...prev, id] : prev.filter(x => x !== id));

  // Calculate reorder total
  const reorderTotal = reorderList.reduce((sum, item) => 
    sum + (item.quantity_needed * item.cost_per_piece), 0
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="inventory-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight flex items-center gap-3">
            <Package className="h-8 w-8 text-indigo-600" />
            Inventory
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage supplies and equipment
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button 
            variant="outline" 
            onClick={() => window.location.href = '/inventory/print-qr'}
            data-testid="print-qr-btn"
          >
            <Printer className="h-4 w-4 mr-2" /> Print QR Labels
          </Button>
          {isAdmin && (
            <Button 
              variant="outline" 
              onClick={() => setUploadDialogOpen(true)} 
              data-testid="upload-csv-btn"
            >
              <Upload className="h-4 w-4 mr-2" /> Upload CSV
            </Button>
          )}
          <Button variant="outline" onClick={handleExportCSV} data-testid="export-csv-btn">
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
          <Button
            onClick={() => {
              resetForm();
              setDialogOpen(true);
            }}
            data-testid="add-inventory-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> Add Item
          </Button>
        </div>
      </div>

      {/* Budget & Supply Tracking Cards - Hidden in staff/simple view */}
      {supplyData && viewMode !== "staff" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-green-200 bg-green-50/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-green-700 flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Available Budget
                </span>
                {isAdmin && !editingBudget && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 w-6 p-0 hover:bg-green-100"
                    onClick={() => setEditingBudget(true)}
                  >
                    <Edit3 className="h-3 w-3" />
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {editingBudget ? (
                <div className="flex gap-2">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={budgetInput}
                    onChange={(e) => setBudgetInput(e.target.value)}
                    className="h-8 text-lg"
                  />
                  <Button size="sm" className="h-8" onClick={handleUpdateBudget}>
                    <Save className="h-3 w-3" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditingBudget(false)}>
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ) : (
                <>
                  <p className="text-2xl font-bold text-green-700" data-testid="available-budget">
                    {formatCurrency(supplyData.available_funds)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">Total supply budget</p>
                </>
              )}
            </CardContent>
          </Card>

          <Card className="border-amber-200 bg-amber-50/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-amber-700 flex items-center gap-2">
                <TrendingDown className="h-4 w-4" />
                Committed Funds
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-amber-700" data-testid="committed-funds">
                {formatCurrency(supplyData.committed_funds)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Locked in approved programs</p>
            </CardContent>
          </Card>

          <Card className={`border-blue-200 ${(supplyData.remaining_funds || 0) < 0 ? 'bg-red-50/30 border-red-200' : 'bg-blue-50/30'}`}>
            <CardHeader className="pb-2">
              <CardTitle className={`text-sm font-medium flex items-center gap-2 ${(supplyData.remaining_funds || 0) < 0 ? 'text-red-700' : 'text-blue-700'}`}>
                <DollarSign className="h-4 w-4" />
                Remaining Budget
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className={`text-2xl font-bold ${(supplyData.remaining_funds || 0) < 0 ? 'text-red-700' : 'text-blue-700'}`} data-testid="remaining-funds">
                {formatCurrency(supplyData.remaining_funds)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Available for new programs</p>
            </CardContent>
          </Card>

          <Card className="border-purple-200 bg-purple-50/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-purple-700 flex items-center gap-2">
                <Boxes className="h-4 w-4" />
                Supply Overages
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-purple-700" data-testid="total-overages">
                {supplyData.total_overage_items || 0} items
              </p>
              <p className="text-xs text-muted-foreground mt-1">Available from past programs</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="inventory" data-testid="tab-inventory">
            <Package className="h-4 w-4 mr-2" /> Inventory ({items.length})
          </TabsTrigger>
          <TabsTrigger value="reorder" data-testid="tab-reorder">
            <ShoppingCart className="h-4 w-4 mr-2" /> Reorder List ({reorderList.length})
          </TabsTrigger>
        </TabsList>

        {/* Notification Cards */}
        <div className="mt-4 space-y-3">
          {/* New Items Available Card */}
          {showNewItemsCard && recentItems.length > 0 && (
            <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200" data-testid="new-items-card">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-100 rounded-lg">
                      <Sparkles className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-medium text-emerald-800">
                        {recentItems.length} New Item{recentItems.length > 1 ? 's' : ''} Available
                      </p>
                      <p className="text-sm text-emerald-600">
                        Added in the last 7 days: {recentItems.slice(0, 3).map(i => i.name).join(', ')}
                        {recentItems.length > 3 ? ` +${recentItems.length - 3} more` : ''}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowNewItemsCard(false)}
                    className="text-emerald-600 hover:text-emerald-800"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* My Active Holds Card */}
          {myHolds.length > 0 && (
            <Card className="bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200" data-testid="my-holds-card">
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Timer className="h-5 w-5 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-purple-800 mb-2">
                      You have {myHolds.length} item{myHolds.length > 1 ? 's' : ''} on hold
                    </p>
                    <div className="space-y-2">
                      {myHolds.map((item) => (
                        <div key={item.id} className="flex items-center justify-between text-sm">
                          <span className="text-purple-700">{item.name}</span>
                          <div className="flex items-center gap-2">
                            {item.expired ? (
                              <Badge className="bg-red-100 text-red-700 border-red-200">
                                <AlertTriangle className="h-3 w-3 mr-1" /> Expired
                              </Badge>
                            ) : (
                              <Badge className={`${item.days_remaining <= 1 ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-purple-100 text-purple-700 border-purple-200'}`}>
                                <Clock className="h-3 w-3 mr-1" /> {item.days_remaining} day{item.days_remaining !== 1 ? 's' : ''} left
                              </Badge>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRelease(item)}
                              className="h-7 text-xs"
                            >
                              Release
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Inventory Tab */}
        <TabsContent value="inventory" className="mt-6">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search items or QR codes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-inventory"
              />
            </div>
            <Select value={locationFilter} onValueChange={setLocationFilter}>
              <SelectTrigger className="w-48" data-testid="filter-location">
                <MapPin className="h-4 w-4 mr-2" />
                <SelectValue placeholder="All Locations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                {locations.locations.map(loc => (
                  <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="filter-status">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="available">Available</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="hold">On Hold</SelectItem>
                <SelectItem value="low_stock">Low Stock</SelectItem>
              </SelectContent>
            </Select>
            <Select value={containerFilter} onValueChange={setContainerFilter}>
              <SelectTrigger className="w-40" data-testid="filter-container">
                <Box className="h-4 w-4 mr-2" />
                <SelectValue placeholder="All Containers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Containers</SelectItem>
                <SelectItem value="box">Boxes Only</SelectItem>
                <SelectItem value="bag">Bags Only</SelectItem>
                <SelectItem value="none">No Container</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Items Table */}
          {filteredItems.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Package className="h-12 w-12 text-muted-foreground mb-4" strokeWidth={1} />
                <p className="font-medium">No inventory items found</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Add your first item to get started
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
            <InventoryBulkDelete
              selectedIds={visibleSelectedIds}
              onClear={() => setSelectedIds([])}
              onDeleted={() => { setSelectedIds([]); fetchData(); }}
            />
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        checked={allVisibleSelected}
                        onCheckedChange={(c) => toggleSelectAll(!!c)}
                        aria-label="Select all items"
                        data-testid="select-all-items"
                      />
                    </TableHead>
                    <TableHead>Item</TableHead>
                    <TableHead>QR Code</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead className="text-center">Qty</TableHead>
                    <TableHead className="text-center">Min</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                    <TableHead>Link</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredItems.map((item) => (
                    <TableRow
                      key={item.id}
                      data-testid={`inventory-row-${item.id}`}
                      className={selectedIds.includes(item.id) ? "bg-red-50/40" : ""}
                    >
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.includes(item.id)}
                          onCheckedChange={(c) => toggleSelect(item.id, !!c)}
                          aria-label={`Select ${item.name}`}
                          data-testid={`select-item-${item.id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div>
                            <p className="font-medium">{item.name}</p>
                            <div className="flex items-center gap-1 mt-1 flex-wrap">
                              {item.container_type && item.container_type !== "none" && (
                                <Badge variant="secondary" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                                  {item.container_size?.toUpperCase()} {item.container_type === "box" ? "Box" : "Bag"}
                                </Badge>
                              )}
                              {item.library_use_only && (
                                <Badge variant="outline" className="text-xs">
                                  <Lock className="h-3 w-3 mr-1" /> Library Only
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <code className="text-xs bg-stone-100 px-2 py-1 rounded">
                          {item.qr_code}
                        </code>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {/* Show primary location or first location from quantities */}
                          {item.location || (item.location_quantities?.[0]?.location) || "No location"}
                          {item.sub_location && (
                            <span className="text-muted-foreground"> - {item.sub_location}</span>
                          )}
                          {/* Show location breakdown if multiple locations */}
                          {item.location_quantities?.length > 1 && (
                            <details className="mt-1">
                              <summary className="text-xs text-blue-600 cursor-pointer hover:text-blue-800">
                                {item.location_quantities.length} locations
                              </summary>
                              <div className="mt-1 space-y-0.5 pl-2 border-l-2 border-blue-100">
                                {item.location_quantities.map((lq, idx) => (
                                  <div key={idx} className="text-xs text-muted-foreground flex justify-between pr-2">
                                    <span>
                                      {lq.location}
                                      {lq.sub_location && ` - ${lq.sub_location}`}
                                    </span>
                                    <span className="font-medium text-foreground">{lq.quantity}</span>
                                  </div>
                                ))}
                              </div>
                            </details>
                          )}
                          {item.location_quantities?.length === 1 && (
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {item.location_quantities[0].sub_location && (
                                <span>{item.location_quantities[0].sub_location}: </span>
                              )}
                              <span className="font-medium">{item.location_quantities[0].quantity}</span>
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center font-medium">
                        {item.quantity}
                      </TableCell>
                      <TableCell className="text-center text-muted-foreground">
                        {item.minimum_stock || "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        ${item.cost_per_piece?.toFixed(2)}
                      </TableCell>
                      <TableCell>
                        {item.purchase_link ? (
                          <a
                            href={item.purchase_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`purchase-link-${item.id}`}
                          >
                            <ExternalLink className="h-3 w-3" />
                            Buy
                          </a>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={STATUS_COLORS[item.status] || "bg-gray-100"}>
                          {STATUS_LABELS[item.status] || item.status}
                        </Badge>
                        {item.status === "hold" && (
                          <div className="mt-1">
                            {item.hold_by_name && (
                              <p className="text-xs text-muted-foreground">
                                by {item.hold_by_name}
                              </p>
                            )}
                            {item.hold_expires_at && (
                              <p className="text-xs text-amber-600 flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {(() => {
                                  const now = new Date();
                                  const expires = new Date(item.hold_expires_at);
                                  const daysLeft = Math.max(0, Math.ceil((expires - now) / (1000 * 60 * 60 * 24)));
                                  return daysLeft <= 0 ? "Expired" : `${daysLeft}d left`;
                                })()}
                              </p>
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`item-menu-${item.id}`}>
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEdit(item)} data-testid={`edit-item-${item.id}`}>
                              <Pencil className="h-4 w-4 mr-2" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDownloadQR(item)}>
                              <QrCode className="h-4 w-4 mr-2" /> Download Label
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {item.status === "pending" && (
                              <DropdownMenuItem onClick={() => {
                                setSelectedItem(item);
                                setReceiveData({ location: item.location || "", sub_location: "", quantity: item.quantity });
                                setReceiveDialogOpen(true);
                              }}>
                                <CheckCircle className="h-4 w-4 mr-2" /> Mark Received
                              </DropdownMenuItem>
                            )}
                            {item.status === "available" && !item.library_use_only && (
                              <DropdownMenuItem onClick={() => {
                                setSelectedItem(item);
                                setHoldDialogOpen(true);
                              }}>
                                <Lock className="h-4 w-4 mr-2" /> Place on Hold
                              </DropdownMenuItem>
                            )}
                            {item.status === "hold" && (
                              <DropdownMenuItem onClick={() => handleRelease(item)}>
                                <Unlock className="h-4 w-4 mr-2" /> Release Hold
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => handleAddToReorder(item)}>
                              <ShoppingCart className="h-4 w-4 mr-2" /> Add to Reorder
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-red-600"
                              data-testid={`delete-item-${item.id}`}
                              onClick={() => {
                                setSelectedItem(item);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="h-4 w-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
            </>
          )}
        </TabsContent>

        {/* Reorder List Tab */}
        <TabsContent value="reorder" className="mt-6">
          <Card className="mb-4 bg-gradient-to-r from-indigo-50 to-blue-50 border-indigo-200">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Reorder Cost</p>
                  <p className="text-2xl font-bold text-indigo-700">${reorderTotal.toFixed(2)}</p>
                </div>
                <div className="flex items-center gap-2">
                  {reorderList.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        try {
                          const response = await axios.get(`${API}/reorder-list/export/csv`, { responseType: 'blob' });
                          const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
                          const link = document.createElement('a');
                          link.href = downloadUrl;
                          link.setAttribute('download', `reorder_list_${new Date().toISOString().split('T')[0]}.csv`);
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                          toast.success("Reorder list exported!");
                        } catch (error) {
                          toast.error("Failed to export reorder list");
                        }
                      }}
                      data-testid="download-reorder-csv"
                    >
                      <Download className="h-4 w-4 mr-2" /> Export CSV
                    </Button>
                  )}
                  <DollarSign className="h-10 w-10 text-indigo-300" />
                </div>
              </div>
            </CardContent>
          </Card>

          {reorderList.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <ShoppingCart className="h-12 w-12 text-muted-foreground mb-4" strokeWidth={1} />
                <p className="font-medium">Reorder list is empty</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Scan items or add from inventory to build your reorder list
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead>QR Code</TableHead>
                    <TableHead className="text-center">Qty Needed</TableHead>
                    <TableHead className="text-right">Cost/Ea</TableHead>
                    <TableHead className="text-right">Subtotal</TableHead>
                    <TableHead>Added By</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reorderList.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.inventory_item_name}</TableCell>
                      <TableCell>
                        <code className="text-xs bg-stone-100 px-2 py-1 rounded">
                          {item.inventory_qr_code}
                        </code>
                      </TableCell>
                      <TableCell className="text-center">
                        <Input
                          type="number"
                          min="1"
                          value={item.quantity_needed}
                          onChange={(e) => {
                            const newQty = parseInt(e.target.value) || 1;
                            setReorderList(prev => prev.map(i => 
                              i.id === item.id ? { ...i, quantity_needed: newQty } : i
                            ));
                          }}
                          onBlur={(e) => {
                            const newQty = parseInt(e.target.value) || 1;
                            if (newQty !== item.quantity_needed) {
                              handleUpdateReorderQuantity(item, newQty);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.target.blur();
                            }
                          }}
                          className="w-16 h-8 text-center"
                          data-testid={`reorder-qty-${item.id}`}
                        />
                      </TableCell>
                      <TableCell className="text-right">${item.cost_per_piece?.toFixed(2)}</TableCell>
                      <TableCell className="text-right font-medium">
                        ${(item.quantity_needed * item.cost_per_piece).toFixed(2)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {item.added_by_name}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveFromReorder(item)}
                        >
                          <XCircle className="h-4 w-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2">
              <Package className="h-6 w-6 text-indigo-600" />
              {isEditing ? "Edit Item" : "Add Inventory Item"}
            </DialogTitle>
            <DialogDescription>
              {isEditing ? "Update item details" : "Add a new item to inventory"}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6 mt-4">
            {/* QR Code Option (new items only) */}
            {!isEditing && (
              <div className="p-4 bg-stone-50 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="font-medium">QR Code Assignment</Label>
                  <Switch
                    checked={useExistingQr}
                    onCheckedChange={setUseExistingQr}
                  />
                </div>
                <p className="text-sm text-muted-foreground">
                  {useExistingQr 
                    ? "Assign an existing QR code to this item" 
                    : "A new QR code will be generated automatically"}
                </p>
                {useExistingQr && (
                  <Input
                    placeholder="Enter existing QR code (e.g., INV-XXXXXXXX)"
                    value={formData.existing_qr_code}
                    onChange={(e) => setFormData({ ...formData, existing_qr_code: e.target.value })}
                    data-testid="existing-qr-input"
                  />
                )}
              </div>
            )}

            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label htmlFor="name">Item Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Craft Scissors, Markers"
                  required
                  data-testid="item-name-input"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description..."
                  rows={2}
                />
              </div>
            </div>

            {/* Location */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Primary Location</Label>
                <Select
                  value={formData.location}
                  onValueChange={(value) => setFormData({ ...formData, location: value, sub_location: "" })}
                >
                  <SelectTrigger data-testid="location-select">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.locations.map(loc => (
                      <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Sub-Location</Label>
                {formData.location === "Staff Kitchen" ? (
                  <Input
                    placeholder="Locker # (e.g., 7)"
                    value={formData.sub_location}
                    onChange={(e) => setFormData({ ...formData, sub_location: e.target.value })}
                    data-testid="locker-input"
                  />
                ) : getSubLocations(formData.location).length > 0 ? (
                  <Select
                    value={formData.sub_location}
                    onValueChange={(value) => setFormData({ ...formData, sub_location: value === "none" ? "" : value })}
                  >
                    <SelectTrigger data-testid="sub-location-select">
                      <SelectValue placeholder="Select sub-location" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {getSubLocations(formData.location).map(sub => (
                        <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    placeholder="Optional sub-location"
                    value={formData.sub_location}
                    onChange={(e) => setFormData({ ...formData, sub_location: e.target.value })}
                  />
                )}
              </div>
            </div>

            {/* Multi-Location Management (shown when editing or when there are multiple locations) */}
            {(isEditing || formData.location_quantities?.length > 0) && (
              <div className="p-4 border border-blue-200 rounded-lg bg-blue-50/50 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-blue-600" />
                    <Label className="font-medium text-blue-800">Stock by Location</Label>
                  </div>
                  <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-300">
                    Total: {formData.location_quantities?.reduce((sum, lq) => sum + (parseFloat(lq.quantity) || 0), 0) || formData.quantity}
                  </Badge>
                </div>
                
                {/* Existing Location Quantities */}
                {formData.location_quantities?.length > 0 && (
                  <div className="space-y-2">
                    {formData.location_quantities.map((lq, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-white rounded border">
                        <Select
                          value={lq.location}
                          onValueChange={(value) => {
                            const newLqs = [...formData.location_quantities];
                            newLqs[idx] = { ...lq, location: value, sub_location: "" };
                            setFormData({ ...formData, location_quantities: newLqs });
                          }}
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder="Location" />
                          </SelectTrigger>
                          <SelectContent>
                            {locations.locations.map(loc => (
                              <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          placeholder="Sub-loc"
                          value={lq.sub_location || ""}
                          onChange={(e) => {
                            const newLqs = [...formData.location_quantities];
                            newLqs[idx] = { ...lq, sub_location: e.target.value };
                            setFormData({ ...formData, location_quantities: newLqs });
                          }}
                          className="w-28"
                        />
                        <Input
                          type="number"
                          min="0"
                          step="0.25"
                          value={lq.quantity}
                          onChange={(e) => {
                            const newLqs = [...formData.location_quantities];
                            newLqs[idx] = { ...lq, quantity: parseFloat(e.target.value) || 0 };
                            setFormData({ ...formData, location_quantities: newLqs });
                          }}
                          className="w-20"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => {
                            const newLqs = formData.location_quantities.filter((_, i) => i !== idx);
                            setFormData({ ...formData, location_quantities: newLqs });
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Add New Location */}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full border-dashed border-blue-300 text-blue-600 hover:bg-blue-100"
                  onClick={() => {
                    setFormData({
                      ...formData,
                      location_quantities: [
                        ...(formData.location_quantities || []),
                        { location: "", sub_location: "", quantity: 0 }
                      ]
                    });
                  }}
                >
                  <Plus className="h-4 w-4 mr-1" /> Add Location
                </Button>
              </div>
            )}

            {/* Quantity & Cost */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="quantity">Quantity</Label>
                <Input
                  id="quantity"
                  type="number"
                  min="0"
                  step="0.25"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: parseFloat(e.target.value) || 0 })}
                  data-testid="quantity-input"
                />
              </div>
              <div>
                <Label htmlFor="minimum_stock">Minimum Stock (optional)</Label>
                <Input
                  id="minimum_stock"
                  type="number"
                  min="0"
                  step="0.25"
                  value={formData.minimum_stock || ""}
                  onChange={(e) => setFormData({ ...formData, minimum_stock: e.target.value ? parseFloat(e.target.value) : null })}
                  placeholder="Reorder threshold"
                  data-testid="min-stock-input"
                />
              </div>
              <div>
                <Label htmlFor="cost_per_piece">Cost per Piece ($)</Label>
                <Input
                  id="cost_per_piece"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.cost_per_piece}
                  onChange={(e) => setFormData({ ...formData, cost_per_piece: parseFloat(e.target.value) || 0 })}
                  data-testid="cost-input"
                />
              </div>
            </div>

            {/* Container Type & Size */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Container Type</Label>
                <Select
                  value={formData.container_type}
                  onValueChange={(value) => setFormData({ 
                    ...formData, 
                    container_type: value,
                    container_size: value === "none" ? "none" : formData.container_size
                  })}
                >
                  <SelectTrigger className="mt-1" data-testid="container-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="box">Box</SelectItem>
                    <SelectItem value="bag">Bag</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.container_type !== "none" && (
                <div>
                  <Label>Size</Label>
                  <Select
                    value={formData.container_size}
                    onValueChange={(value) => setFormData({ ...formData, container_size: value })}
                  >
                    <SelectTrigger className="mt-1" data-testid="container-size-select">
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sm">Small (SM)</SelectItem>
                      <SelectItem value="m">Medium (M)</SelectItem>
                      <SelectItem value="l">Large (L)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Quick Quantity Adjustment (Edit mode only) */}
            {isEditing && (
              <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg space-y-3">
                <div className="flex items-center gap-2">
                  <Package className="h-5 w-5 text-indigo-600" />
                  <Label className="font-medium text-indigo-800">Quick Quantity Adjustment</Label>
                </div>
                <p className="text-sm text-indigo-600">
                  Quickly add or remove stock by location
                </p>
                
                {/* Current stock by location summary */}
                {formData.location_quantities?.length > 0 && (
                  <div className="bg-white rounded-lg p-3 border border-indigo-100">
                    <p className="text-xs text-muted-foreground mb-2">Current stock by location:</p>
                    <div className="space-y-1">
                      {formData.location_quantities.map((lq, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span>{lq.location}{lq.sub_location ? ` - ${lq.sub_location}` : ""}</span>
                          <span className="font-medium">{lq.quantity}</span>
                        </div>
                      ))}
                      <div className="pt-1 mt-1 border-t flex justify-between text-sm font-medium">
                        <span>Total</span>
                        <span>{formData.location_quantities.reduce((sum, lq) => sum + (parseFloat(lq.quantity) || 0), 0)}</span>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="h-12 text-red-600 border-red-300 hover:bg-red-50"
                    onClick={() => {
                      setQuickSubtractData({ location: "", quantity: 1 });
                      setQuickSubtractOpen(true);
                    }}
                    disabled={formData.quantity <= 0}
                    data-testid="quick-subtract-btn"
                  >
                    <Minus className="h-4 w-4 mr-2" /> Use from Stock
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-12 text-green-600 border-green-300 hover:bg-green-50"
                    onClick={() => {
                      setQuickAddData({ 
                        location: formData.location_quantities?.[0]?.location || formData.location || "", 
                        sub_location: formData.location_quantities?.[0]?.sub_location || formData.sub_location || "",
                        quantity: 1, 
                        isNewLocation: false 
                      });
                      setQuickAddOpen(true);
                    }}
                    data-testid="quick-add-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" /> Add to Stock
                  </Button>
                </div>
                
                <p className="text-xs text-indigo-500 mt-2">
                  Total quantity: <span className="font-semibold">{formData.quantity}</span>
                  {selectedItem && selectedItem.quantity !== formData.quantity && (
                    <span className="ml-2">
                      (was {selectedItem.quantity}, 
                      <span className={formData.quantity > selectedItem.quantity ? "text-green-600" : "text-red-600"}>
                        {" "}{formData.quantity > selectedItem.quantity ? "+" : ""}{formData.quantity - selectedItem.quantity}
                      </span>)
                    </span>
                  )}
                </p>
              </div>
            )}

            {/* Expiration */}
            <div className="p-4 border rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-medium">Has Expiration Date</Label>
                <Switch
                  checked={formData.has_expiration}
                  onCheckedChange={(checked) => setFormData({ ...formData, has_expiration: checked })}
                  data-testid="has-expiration-toggle"
                />
              </div>
              {formData.has_expiration && (
                <Input
                  type="date"
                  value={formData.expiration_date}
                  onChange={(e) => setFormData({ ...formData, expiration_date: e.target.value })}
                  data-testid="expiration-date-input"
                />
              )}
            </div>

            {/* Library Use Only (Supervisor only) */}
            {isSupervisor && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="font-medium text-amber-800">Library Use Only</Label>
                    <p className="text-sm text-amber-600">
                      These items cannot be dedicated to programs
                    </p>
                  </div>
                  <Switch
                    checked={formData.library_use_only}
                    onCheckedChange={(checked) => setFormData({ ...formData, library_use_only: checked })}
                    data-testid="library-only-toggle"
                  />
                </div>
              </div>
            )}

            {/* Notes */}
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional notes..."
                rows={2}
              />
            </div>

            {/* Purchase Link */}
            <div>
              <Label htmlFor="purchase_link">Purchase Link (for reordering)</Label>
              <Input
                id="purchase_link"
                type="url"
                value={formData.purchase_link}
                onChange={(e) => setFormData({ ...formData, purchase_link: e.target.value })}
                placeholder="https://amazon.com/dp/..."
                data-testid="purchase-link-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Add a direct link to purchase this item for easy reordering
              </p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="save-item-btn">
                {isEditing ? "Update Item" : "Add Item"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Item</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{selectedItem?.name}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="delete-confirm-btn">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Hold Dialog */}
      <Dialog open={holdDialogOpen} onOpenChange={setHoldDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Place Item on Hold</DialogTitle>
            <DialogDescription>
              Reserve "{selectedItem?.name}" for your project
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Location Selection - only show if item has multiple locations */}
            {selectedItem?.location_quantities?.length > 1 && (
              <div>
                <Label htmlFor="holdLocation">Hold from Location *</Label>
                <Select
                  value={holdLocation}
                  onValueChange={setHoldLocation}
                >
                  <SelectTrigger data-testid="hold-location-select">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedItem.location_quantities.map((lq, idx) => (
                      <SelectItem key={idx} value={lq.location}>
                        {lq.location} {lq.sub_location && `- ${lq.sub_location}`} ({lq.quantity} available)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {/* Show single location info */}
            {selectedItem?.location_quantities?.length === 1 && (
              <div className="p-3 bg-stone-50 rounded-lg">
                <p className="text-sm text-muted-foreground">Location:</p>
                <p className="font-medium">
                  {selectedItem.location_quantities[0].location}
                  {selectedItem.location_quantities[0].sub_location && ` - ${selectedItem.location_quantities[0].sub_location}`}
                  <span className="text-muted-foreground ml-2">
                    ({selectedItem.location_quantities[0].quantity} available)
                  </span>
                </p>
              </div>
            )}
            <div>
              <Label htmlFor="holdReason">Reason (optional)</Label>
              <Input
                id="holdReason"
                value={holdReason}
                onChange={(e) => setHoldReason(e.target.value)}
                placeholder="e.g., Summer reading program prep"
              />
            </div>
            <p className="text-sm text-muted-foreground">
              Items on hold will be automatically released after 5 days
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setHoldDialogOpen(false);
              setHoldReason("");
              setHoldLocation("");
            }}>Cancel</Button>
            <Button 
              onClick={handleHold}
              disabled={selectedItem?.location_quantities?.length > 1 && !holdLocation}
            >
              Place on Hold
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receive Dialog */}
      <Dialog open={receiveDialogOpen} onOpenChange={setReceiveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Receive Item</DialogTitle>
            <DialogDescription>
              Mark "{selectedItem?.name}" as received and assign a location
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>Location *</Label>
              <Select
                value={receiveData.location}
                onValueChange={(value) => setReceiveData({ ...receiveData, location: value, sub_location: "" })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.locations.map(loc => (
                    <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {receiveData.location === "Staff Kitchen" ? (
              <div>
                <Label>Locker #</Label>
                <Input
                  placeholder="e.g., 7"
                  value={receiveData.sub_location}
                  onChange={(e) => setReceiveData({ ...receiveData, sub_location: e.target.value })}
                />
              </div>
            ) : getSubLocations(receiveData.location).length > 0 && (
              <div>
                <Label>Sub-Location</Label>
                <Select
                  value={receiveData.sub_location}
                  onValueChange={(value) => setReceiveData({ ...receiveData, sub_location: value === "none" ? "" : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select sub-location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {getSubLocations(receiveData.location).map(sub => (
                      <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Quantity Received</Label>
              <Input
                type="number"
                min="0"
                value={receiveData.quantity || ""}
                onChange={(e) => setReceiveData({ ...receiveData, quantity: parseInt(e.target.value) || null })}
                placeholder={`Expected: ${selectedItem?.quantity}`}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceiveDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleReceive}>Mark Received</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Upload Dialog (Admin Only) */}
      <Dialog open={uploadDialogOpen} onOpenChange={(open) => { if (!open) resetUploadDialog(); else setUploadDialogOpen(true); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-indigo-600" />
              Bulk Upload Inventory
            </DialogTitle>
            <DialogDescription>
              Upload a CSV file to add multiple inventory items at once. Only administrators can use this feature.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Template Download */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Download className="h-5 w-5 text-blue-600 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-blue-900">Download Template First</p>
                  <p className="text-sm text-blue-700 mt-1">
                    Get the CSV template with example data and all supported fields.
                  </p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-2"
                    onClick={handleDownloadTemplate}
                  >
                    <Download className="h-4 w-4 mr-2" /> Download Template
                  </Button>
                </div>
              </div>
            </div>

            {/* File Upload */}
            <div>
              <Label>Select CSV File</Label>
              <div className="mt-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-md file:border-0
                    file:text-sm file:font-semibold
                    file:bg-indigo-50 file:text-indigo-700
                    hover:file:bg-indigo-100
                    cursor-pointer"
                  data-testid="csv-file-input"
                />
              </div>
              {uploadFile && (
                <p className="text-sm text-muted-foreground mt-2">
                  Selected: {uploadFile.name}
                </p>
              )}
            </div>

            {/* Upload Results */}
            {uploadResults && (
              <div className="space-y-3">
                <div className="flex gap-3">
                  <div className="flex-1 bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-700">{uploadResults.imported}</p>
                    <p className="text-sm text-green-600">Imported</p>
                  </div>
                  <div className="flex-1 bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-amber-700">{uploadResults.skipped_duplicates || 0}</p>
                    <p className="text-sm text-amber-600">Duplicates</p>
                  </div>
                  <div className="flex-1 bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-red-700">{uploadResults.failed}</p>
                    <p className="text-sm text-red-600">Failed</p>
                  </div>
                </div>
                
                {uploadResults.duplicates?.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="font-medium text-amber-800 mb-2">Duplicates Skipped:</p>
                    <ul className="text-sm text-amber-700 space-y-1 max-h-32 overflow-y-auto">
                      {uploadResults.duplicates.slice(0, 10).map((dup, idx) => (
                        <li key={idx}>Row {dup.row}: {dup.name} - {dup.reason}</li>
                      ))}
                      {uploadResults.duplicates.length > 10 && (
                        <li className="text-amber-600">...and {uploadResults.duplicates.length - 10} more</li>
                      )}
                    </ul>
                  </div>
                )}
                
                {uploadResults.errors?.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="font-medium text-red-800 mb-2">Errors:</p>
                    <ul className="text-sm text-red-700 space-y-1 max-h-32 overflow-y-auto">
                      {uploadResults.errors.map((err, idx) => (
                        <li key={idx}>Row {err.row}: {err.error}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {uploadResults.success?.length > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="font-medium text-green-800 mb-2">Successfully imported:</p>
                    <ul className="text-sm text-green-700 space-y-1 max-h-32 overflow-y-auto">
                      {uploadResults.success.slice(0, 10).map((item, idx) => (
                        <li key={idx}>{item.name} ({item.qr_code})</li>
                      ))}
                      {uploadResults.success.length > 10 && (
                        <li className="text-green-600">...and {uploadResults.success.length - 10} more</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={resetUploadDialog}>
              {uploadResults ? "Close" : "Cancel"}
            </Button>
            {!uploadResults && (
              <Button 
                onClick={() => handleUploadCSV(true)} 
                disabled={!uploadFile || uploadLoading}
                data-testid="upload-csv-submit"
              >
                {uploadLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" /> Upload (Skip Duplicates)
                  </>
                )}
              </Button>
            )}
            {uploadResults && uploadResults.skipped_duplicates > 0 && (
              <Button 
                variant="secondary"
                onClick={() => {
                  setUploadResults(null);
                  handleUploadCSV(false);
                }}
                disabled={uploadLoading}
              >
                {uploadLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Importing...
                  </>
                ) : (
                  <>
                    Import Duplicates Anyway ({uploadResults.skipped_duplicates})
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Add Stock Dialog */}
      <Dialog open={quickAddOpen} onOpenChange={setQuickAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <Plus className="h-5 w-5" />
              Add Stock to Location
            </DialogTitle>
            <DialogDescription>
              Choose where to add the received stock
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Existing locations or new location toggle */}
            {formData.location_quantities?.length > 0 && !quickAddData.isNewLocation ? (
              <div className="space-y-3">
                <Label>Select Location</Label>
                <div className="space-y-2">
                  {formData.location_quantities.map((lq, idx) => (
                    <div
                      key={idx}
                      onClick={() => setQuickAddData({ 
                        ...quickAddData, 
                        location: lq.location, 
                        sub_location: lq.sub_location || "" 
                      })}
                      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                        quickAddData.location === lq.location && quickAddData.sub_location === (lq.sub_location || "")
                          ? "border-green-500 bg-green-50" 
                          : "border-stone-200 hover:border-green-300 hover:bg-green-50/50"
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="font-medium">{lq.location}</span>
                        <Badge variant="outline">{lq.quantity} in stock</Badge>
                      </div>
                      {lq.sub_location && (
                        <span className="text-sm text-muted-foreground">{lq.sub_location}</span>
                      )}
                    </div>
                  ))}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full border-dashed"
                  onClick={() => setQuickAddData({ ...quickAddData, location: "", sub_location: "", isNewLocation: true })}
                >
                  <Plus className="h-4 w-4 mr-1" /> Add to New Location
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {formData.location_quantities?.length > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setQuickAddData({ ...quickAddData, isNewLocation: false })}
                    className="text-muted-foreground"
                  >
                    ← Back to existing locations
                  </Button>
                )}
                <div>
                  <Label>Location</Label>
                  <Select
                    value={quickAddData.location}
                    onValueChange={(value) => setQuickAddData({ ...quickAddData, location: value, sub_location: "" })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.locations.map(loc => (
                        <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {quickAddData.location && getSubLocations(quickAddData.location).length > 0 && (
                  <div>
                    <Label>Sub-Location (optional)</Label>
                    <Select
                      value={quickAddData.sub_location || "none"}
                      onValueChange={(value) => setQuickAddData({ ...quickAddData, sub_location: value === "none" ? "" : value })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select sub-location" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {getSubLocations(quickAddData.location).map(sub => (
                          <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {quickAddData.location === "Staff Kitchen" && (
                  <div>
                    <Label>Locker # (optional)</Label>
                    <Input
                      placeholder="e.g., 7"
                      value={quickAddData.sub_location}
                      onChange={(e) => setQuickAddData({ ...quickAddData, sub_location: e.target.value })}
                    />
                  </div>
                )}
              </div>
            )}
            
            <div>
              <Label>Quantity to Add</Label>
              <Input
                type="number"
                min="0.25"
                step="0.25"
                value={quickAddData.quantity}
                onChange={(e) => setQuickAddData({ ...quickAddData, quantity: parseFloat(e.target.value) || 0.25 })}
                className="mt-1"
                data-testid="quick-add-quantity"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuickAddOpen(false)}>Cancel</Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              disabled={!quickAddData.location || quickAddData.quantity < 1}
              onClick={() => {
                const locationQuantities = [...(formData.location_quantities || [])];
                const existingIdx = locationQuantities.findIndex(
                  lq => lq.location === quickAddData.location && (lq.sub_location || "") === (quickAddData.sub_location || "")
                );
                
                if (existingIdx >= 0) {
                  locationQuantities[existingIdx] = {
                    ...locationQuantities[existingIdx],
                    quantity: (locationQuantities[existingIdx].quantity || 0) + quickAddData.quantity
                  };
                } else {
                  locationQuantities.push({
                    location: quickAddData.location,
                    sub_location: quickAddData.sub_location || "",
                    quantity: quickAddData.quantity
                  });
                }
                
                const newTotal = locationQuantities.reduce((sum, lq) => sum + (parseFloat(lq.quantity) || 0), 0);
                setFormData({ 
                  ...formData, 
                  location_quantities: locationQuantities,
                  quantity: newTotal,
                  location: formData.location || quickAddData.location,
                  sub_location: formData.sub_location || quickAddData.sub_location
                });
                setQuickAddOpen(false);
                toast.success(`Added ${quickAddData.quantity} to ${quickAddData.location}`);
              }}
              data-testid="quick-add-confirm"
            >
              <Plus className="h-4 w-4 mr-1" /> Add {quickAddData.quantity}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Subtract Stock Dialog */}
      <Dialog open={quickSubtractOpen} onOpenChange={setQuickSubtractOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Minus className="h-5 w-5" />
              Use from Stock
            </DialogTitle>
            <DialogDescription>
              Choose which location to take items from
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {formData.location_quantities?.length > 0 ? (
              <div className="space-y-3">
                <Label>Select Location</Label>
                <div className="space-y-2">
                  {formData.location_quantities.filter(lq => lq.quantity > 0).map((lq, idx) => (
                    <div
                      key={idx}
                      onClick={() => setQuickSubtractData({ 
                        ...quickSubtractData, 
                        location: lq.location,
                        sub_location: lq.sub_location || "",
                        maxQty: lq.quantity
                      })}
                      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                        quickSubtractData.location === lq.location && quickSubtractData.sub_location === (lq.sub_location || "")
                          ? "border-red-500 bg-red-50" 
                          : "border-stone-200 hover:border-red-300 hover:bg-red-50/50"
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="font-medium">{lq.location}</span>
                        <Badge variant="outline" className={lq.quantity <= 5 ? "text-amber-600 border-amber-300" : ""}>
                          {lq.quantity} available
                        </Badge>
                      </div>
                      {lq.sub_location && (
                        <span className="text-sm text-muted-foreground">{lq.sub_location}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="p-3 bg-stone-50 rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    Location: <span className="font-medium text-foreground">{formData.location || "Not set"}</span>
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Available: <span className="font-medium text-foreground">{formData.quantity}</span>
                  </p>
                </div>
              </div>
            )}
            
            <div>
              <Label>Quantity to Use</Label>
              <Input
                type="number"
                min="0.25"
                step="0.25"
                max={quickSubtractData.maxQty || formData.quantity}
                value={quickSubtractData.quantity}
                onChange={(e) => {
                  const max = quickSubtractData.maxQty || formData.quantity;
                  setQuickSubtractData({ 
                    ...quickSubtractData, 
                    quantity: Math.min(parseFloat(e.target.value) || 0.25, max)
                  });
                }}
                className="mt-1"
                data-testid="quick-subtract-quantity"
              />
              {(quickSubtractData.maxQty || formData.quantity) > 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  Max: {quickSubtractData.maxQty || formData.quantity}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuickSubtractOpen(false)}>Cancel</Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              disabled={
                (formData.location_quantities?.length > 0 && !quickSubtractData.location) || 
                quickSubtractData.quantity < 1 ||
                quickSubtractData.quantity > (quickSubtractData.maxQty || formData.quantity)
              }
              onClick={() => {
                if (formData.location_quantities?.length > 0) {
                  const locationQuantities = [...formData.location_quantities];
                  const idx = locationQuantities.findIndex(
                    lq => lq.location === quickSubtractData.location && (lq.sub_location || "") === (quickSubtractData.sub_location || "")
                  );
                  
                  if (idx >= 0) {
                    locationQuantities[idx] = {
                      ...locationQuantities[idx],
                      quantity: Math.max(0, (locationQuantities[idx].quantity || 0) - quickSubtractData.quantity)
                    };
                    // Remove locations with 0 quantity
                    const filtered = locationQuantities.filter(lq => lq.quantity > 0);
                    const newTotal = filtered.reduce((sum, lq) => sum + (parseFloat(lq.quantity) || 0), 0);
                    setFormData({ 
                      ...formData, 
                      location_quantities: filtered,
                      quantity: newTotal
                    });
                  }
                } else {
                  setFormData({ 
                    ...formData, 
                    quantity: Math.max(0, formData.quantity - quickSubtractData.quantity)
                  });
                }
                setQuickSubtractOpen(false);
                toast.success(`Used ${quickSubtractData.quantity} from stock`);
              }}
              data-testid="quick-subtract-confirm"
            >
              <Minus className="h-4 w-4 mr-1" /> Use {quickSubtractData.quantity}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
